import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Power, Send, UserPlus, MessageSquare, RefreshCw, Check, X, Zap } from "lucide-react";
import {
  api,
  type Recruiter,
  type RecruiterChatLogRow,
  type RecruiterDetail,
  type ManualLaunch,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function DialogFooter({ children }: { children: React.ReactNode }) {
  return <div className="mt-4 flex justify-end gap-2">{children}</div>;
}
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ChatTranscript, type ChatBubbleItem } from "@/components/ChatTranscript";

const TEAM_PHONES = [
  { phone: "+12022994648", name: "Justin" },
  { phone: "+12016730855", name: "Barb" },
];

export function Recruiters() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [onboardingId, setOnboardingId] = React.useState<string | null>(null);

  const list = useQuery({
    queryKey: ["recruiters"],
    queryFn: () => api.recruitersList({ include_inactive: false }),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Recruiters</h1>
          <p className="text-sm text-muted-foreground">
            The team running daily DM outreach. Each gets a private iMessage
            chat with you, Barb, and Kitt.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          <RefreshCw className={"mr-2 h-4 w-4 " + (list.isFetching ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>

      {list.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : !list.data || list.data.length === 0 ? (
        <Empty title="No recruiters" hint="Add one in Supabase recruiters table to start." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recruiter</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Yesterday</TableHead>
              <TableHead>Last 7d</TableHead>
              <TableHead>MTD launches</TableHead>
              <TableHead>Streak miss</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[200px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data.map((r) => (
              <RecruiterRow
                key={r.recruiter_id}
                r={r}
                onOpen={() => setSelectedId(r.recruiter_id)}
                onOnboard={() => setOnboardingId(r.recruiter_id)}
                onSendNow={async () => {
                  try {
                    await api.sendRecruiterCheckinNow(r.recruiter_id);
                    qc.invalidateQueries({ queryKey: ["recruiters"] });
                  } catch (e) {
                    alert(`Send failed: ${(e as Error).message}`);
                  }
                }}
                onToggleKill={async () => {
                  try {
                    await api.toggleRecruiterKillSwitch(
                      r.recruiter_id, !r.chat_kill_switch,
                    );
                    qc.invalidateQueries({ queryKey: ["recruiters"] });
                  } catch (e) {
                    alert(`Toggle failed: ${(e as Error).message}`);
                  }
                }}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {selectedId && (
        <RecruiterDetailDrawer
          recruiterId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {onboardingId && (
        <OnboardDialog
          recruiterId={onboardingId}
          onClose={() => setOnboardingId(null)}
          onDone={() => {
            setOnboardingId(null);
            qc.invalidateQueries({ queryKey: ["recruiters"] });
          }}
        />
      )}
    </div>
  );
}

function RecruiterRow({
  r, onOpen, onOnboard, onSendNow, onToggleKill,
}: {
  r: Recruiter;
  onOpen: () => void;
  onOnboard: () => void;
  onSendNow: () => void;
  onToggleKill: () => void;
}) {
  const dms_yday = r.dms_yday || 0;
  const quota = r.daily_dm_quota || 30;
  const hit = dms_yday >= quota;
  return (
    <TableRow className="cursor-pointer" onClick={onOpen}>
      <TableCell className="font-medium">
        <div>{r.name}</div>
        <div className="text-xs text-muted-foreground">
          {r.phone || "no phone"} {r.linked_creator_username ? `· @${r.linked_creator_username}` : ""}
        </div>
      </TableCell>
      <TableCell>
        <span className="text-xs uppercase text-muted-foreground">{r.recruiter_type}</span>
      </TableCell>
      <TableCell>
        <span className={hit ? "text-emerald-600" : "text-amber-600"}>
          {dms_yday}/{quota}
        </span>
      </TableCell>
      <TableCell>{r.dms_last_7d}</TableCell>
      <TableCell>{r.mtd_launches}</TableCell>
      <TableCell>
        {r.streak_misses > 0 ? (
          <span className="text-rose-600 text-sm">{r.streak_misses}d</span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        {!r.is_onboarded ? (
          <span className="text-xs text-muted-foreground">Not onboarded</span>
        ) : r.chat_kill_switch ? (
          <span className="text-xs text-rose-600">Kill switch ON</span>
        ) : (
          <span className="text-xs text-emerald-600 inline-flex items-center gap-1">
            Active
            {r.midday_pulse_enabled && (
              <span
                className="inline-flex items-center"
                title={`Mid-day pulse on at ${r.midday_pulse_hour}:00`}
                aria-label={`Mid-day pulse on at ${r.midday_pulse_hour}:00`}
              >
                <Zap className="h-3 w-3 text-amber-500" />
              </span>
            )}
          </span>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1">
          {!r.is_onboarded ? (
            <Button size="sm" variant="default" onClick={onOnboard}>
              <UserPlus className="mr-1 h-3 w-3" /> Onboard
            </Button>
          ) : (
            <>
              <Button
                size="sm" variant="outline"
                onClick={onSendNow}
                disabled={r.chat_kill_switch}
                title="Force-fire today's check-in now"
              >
                <Send className="h-3 w-3" />
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={onToggleKill}
                title={r.chat_kill_switch ? "Re-enable Kitt" : "Pause this chat"}
              >
                <Power className={"h-3 w-3 " + (r.chat_kill_switch ? "text-rose-600" : "")} />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function OnboardDialog({
  recruiterId, onClose, onDone,
}: {
  recruiterId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const detail = useQuery({
    queryKey: ["recruiter", recruiterId],
    queryFn: () => api.recruiter(recruiterId),
  });

  const [phone, setPhone] = React.useState("");
  const [kickoff, setKickoff] = React.useState("");

  React.useEffect(() => {
    if (detail.data && !phone) {
      setPhone(detail.data.phone || "");
    }
    if (detail.data && !kickoff) {
      setKickoff(
        `hey ${detail.data.name.split(" ")[0]}! kitt here, getting us into a thread so we can keep in sync on dms and launches. justin and barb are in here too. excited to have you on the team 🙂`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data]);

  const onboard = useMutation({
    mutationFn: () =>
      api.onboardRecruiter(recruiterId, {
        addresses: [phone, ...TEAM_PHONES.map((p) => p.phone)],
        kickoff_text: kickoff,
        enqueue_checkin: true,
      }),
    onSuccess: () => onDone(),
    onError: (e) => alert(`Onboard failed: ${(e as Error).message}`),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Onboard {detail.data?.name ?? "recruiter"}</DialogTitle>
          <DialogDescription>
            Creates an iMessage group chat with the recruiter, Justin, and
            Barb. Kitt sends the kickoff. Daily check-in fires immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Recruiter phone</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+13108820671"
            />
          </div>
          <div>
            <Label>Kickoff message</Label>
            <Textarea
              value={kickoff}
              onChange={(e) => setKickoff(e.target.value)}
              rows={4}
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Group will also include: {TEAM_PHONES.map((p) => `${p.name} (${p.phone})`).join(", ")}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onboard.mutate()}
            disabled={onboard.isPending || !phone || !kickoff}
          >
            {onboard.isPending ? "Creating chat…" : "Create chat & send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecruiterDetailDrawer({
  recruiterId, onClose,
}: {
  recruiterId: string;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: ["recruiter", recruiterId],
    queryFn: () => api.recruiter(recruiterId),
  });
  const chatLog = useQuery({
    queryKey: ["recruiter-chat", recruiterId],
    queryFn: () => api.recruiterChatLog(recruiterId, { limit: 100 }),
    refetchInterval: 10_000,
    enabled: !!detail.data?.is_onboarded,
  });
  const launches = useQuery({
    queryKey: ["recruiter-launches", recruiterId],
    queryFn: () => api.manualLaunches(recruiterId),
    enabled: detail.data?.recruiter_type === "manual",
  });
  const qc = useQueryClient();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{detail.data?.name ?? "Loading…"}</DialogTitle>
          <DialogDescription>
            {detail.data?.recruiter_type} · {detail.data?.phone || "no phone"}
            {detail.data?.linked_creator_username
              ? ` · creator: @${detail.data.linked_creator_username}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {detail.data && (
          <div className="space-y-6">
            {/* Stats */}
            <section className="grid grid-cols-4 gap-4">
              <Stat label="DMs yesterday" value={`${detail.data.dms_yday}/${detail.data.daily_dm_quota}`} />
              <Stat label="DMs last 7d" value={String(detail.data.dms_last_7d)} />
              <Stat label="MTD launches" value={String(detail.data.mtd_launches)} />
              <Stat
                label="Streak miss"
                value={detail.data.streak_misses ? `${detail.data.streak_misses}d` : "—"}
              />
            </section>

            {/* Last check-in */}
            {detail.data.last_checkin && (
              <section className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground mb-1">
                  Last check-in · {detail.data.last_checkin.message_kind} ·{" "}
                  {detail.data.last_checkin.sent_at?.slice(0, 16).replace("T", " ")}
                </div>
                <div className="text-sm whitespace-pre-wrap">
                  {detail.data.last_checkin.kitt_message}
                </div>
              </section>
            )}

            {/* Mid-day pulse (app_dm only) */}
            {detail.data.recruiter_type === "app_dm" && (
              <MiddayPulsePanel
                detail={detail.data}
                onChanged={() => detail.refetch()}
              />
            )}

            {/* Chat transcript */}
            {detail.data.is_onboarded && (
              <section>
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-sm font-medium flex items-center gap-1">
                    <MessageSquare className="h-3.5 w-3.5" /> Chat history
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    auto-refreshing every 10s
                  </span>
                </div>
                <ChatTranscript
                  items={(chatLog.data?.items ?? []).map((m): ChatBubbleItem => ({
                    id: m.id,
                    direction: m.direction,
                    label: m.direction === "outbound" ? "Kitt" : (m.sender_role ?? null),
                    tag: m.ai_intent,
                    timestamp: m.message_timestamp,
                    text: m.message_text,
                  }))}
                  loading={chatLog.isLoading}
                  emptyTitle="No messages yet"
                  emptyHint="The chat exists but no inbound or outbound logged."
                />
              </section>
            )}

            {/* Manual launches */}
            {detail.data.recruiter_type === "manual" && launches.data && launches.data.length > 0 && (
              <section>
                <h3 className="text-sm font-medium mb-2">Manual launches reported</h3>
                <ManualLaunchesTable
                  items={launches.data}
                  onConfirm={async (id) => {
                    try {
                      await api.confirmManualLaunch(id);
                      qc.invalidateQueries({ queryKey: ["recruiter-launches", recruiterId] });
                    } catch (e) {
                      alert(`Confirm failed: ${(e as Error).message}`);
                    }
                  }}
                  onReject={async (id) => {
                    const r = prompt("Why reject?");
                    if (r === null) return;
                    try {
                      await api.rejectManualLaunch(id, r);
                      qc.invalidateQueries({ queryKey: ["recruiter-launches", recruiterId] });
                    } catch (e) {
                      alert(`Reject failed: ${(e as Error).message}`);
                    }
                  }}
                />
              </section>
            )}

            {/* Quotas + actions */}
            <section className="rounded-md border p-3 space-y-3">
              <h3 className="text-sm font-medium">Settings</h3>
              <QuotaForm detail={detail.data} onSaved={() => detail.refetch()} />
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm" variant="outline"
                  onClick={async () => {
                    try {
                      await api.sendRecruiterCheckinNow(recruiterId);
                      alert("Check-in queued. It'll land in the chat in a few seconds.");
                    } catch (e) {
                      alert(`Send-now failed: ${(e as Error).message}`);
                    }
                  }}
                  disabled={!detail.data.is_onboarded || detail.data.chat_kill_switch}
                >
                  <Send className="mr-1 h-3 w-3" /> Send check-in now
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={async () => {
                    try {
                      await api.toggleRecruiterKillSwitch(
                        recruiterId, !detail.data!.chat_kill_switch,
                      );
                      detail.refetch();
                    } catch (e) {
                      alert(`Toggle failed: ${(e as Error).message}`);
                    }
                  }}
                >
                  <Power className="mr-1 h-3 w-3" />
                  {detail.data.chat_kill_switch ? "Re-enable Kitt" : "Pause this chat"}
                </Button>
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function MiddayPulsePanel({
  detail, onChanged,
}: {
  detail: RecruiterDetail;
  onChanged: () => void;
}) {
  const [hourInput, setHourInput] = React.useState(String(detail.midday_pulse_hour));
  const [saving, setSaving] = React.useState(false);
  const hourChanged = hourInput !== String(detail.midday_pulse_hour);

  async function toggle() {
    setSaving(true);
    try {
      await api.updateRecruiterMiddayPulse(detail.recruiter_id, {
        enabled: !detail.midday_pulse_enabled,
      });
      onChanged();
    } catch (e) {
      alert(`Toggle failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function saveHour() {
    const h = Number(hourInput);
    if (!Number.isInteger(h) || h < 0 || h > 23) {
      alert("Hour must be an integer 0–23.");
      return;
    }
    setSaving(true);
    try {
      await api.updateRecruiterMiddayPulse(detail.recruiter_id, { hour: h });
      onChanged();
    } catch (e) {
      alert(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function sendNow() {
    try {
      await api.sendRecruiterMiddayPulseNow(detail.recruiter_id);
      alert("Mid-day pulse queued. It'll land in a few seconds (unless quota's already hit).");
    } catch (e) {
      alert(`Send failed: ${(e as Error).message}`);
    }
  }

  return (
    <section className="rounded-md border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium flex items-center gap-1">
          <Zap className="h-3.5 w-3.5" /> Mid-day pulse
        </h3>
        <span className="text-xs text-muted-foreground">
          {detail.midday_pulse_enabled
            ? `enabled · fires ~${detail.midday_pulse_hour}:00 ${detail.checkin_timezone}`
            : "disabled"}
        </span>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <Button
          size="sm" variant="outline" onClick={toggle} disabled={saving}
        >
          {detail.midday_pulse_enabled ? "Turn off" : "Turn on"}
        </Button>
        <label className="flex items-center gap-1 ml-2">
          <span className="text-xs text-muted-foreground">Hour</span>
          <Input
            type="number" min={0} max={23}
            value={hourInput}
            onChange={(e) => setHourInput(e.target.value)}
            className="w-16 h-7"
          />
        </label>
        <Button
          size="sm" variant="outline" onClick={saveHour}
          disabled={saving || !hourChanged}
        >
          Save hour
        </Button>
        <Button
          size="sm" variant="outline" onClick={sendNow}
          disabled={!detail.is_onboarded || detail.chat_kill_switch}
          title="Fire a pulse outside the scheduled window"
        >
          <Send className="mr-1 h-3 w-3" /> Pulse now
        </Button>
      </div>

      {detail.last_midday_pulse && (
        <div className="rounded-md bg-muted/40 p-2">
          <div className="text-xs text-muted-foreground mb-1">
            Last pulse · {detail.last_midday_pulse.message_kind} ·{" "}
            {detail.last_midday_pulse.sent_at?.slice(0, 16).replace("T", " ")}
          </div>
          <div className="text-sm whitespace-pre-wrap">
            {detail.last_midday_pulse.kitt_message}
          </div>
        </div>
      )}
    </section>
  );
}

function ManualLaunchesTable({
  items, onConfirm, onReject,
}: {
  items: ManualLaunch[];
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Club / creator</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((m) => (
          <TableRow key={m.id}>
            <TableCell>{m.club_name || m.creator_username || "—"}</TableCell>
            <TableCell>{m.launched_on || "—"}</TableCell>
            <TableCell>
              <span
                className={
                  "text-xs " +
                  (m.status === "confirmed" ? "text-emerald-600"
                    : m.status === "rejected" ? "text-rose-600"
                    : "text-amber-600")
                }
              >
                {m.status}
              </span>
            </TableCell>
            <TableCell>
              {m.status === "reported" ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => onConfirm(m.id)}>
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onReject(m.id)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function QuotaForm({
  detail, onSaved,
}: {
  detail: NonNullable<ReturnType<typeof api.recruiter> extends Promise<infer R> ? R : never>;
  onSaved: () => void;
}) {
  const [daily, setDaily] = React.useState(String(detail.daily_dm_quota ?? 30));
  const [monthly, setMonthly] = React.useState(String(detail.monthly_launch_quota ?? ""));
  const [hour, setHour] = React.useState(String(detail.checkin_hour ?? 10));
  const [tz, setTz] = React.useState(detail.checkin_timezone ?? "America/New_York");
  const save = useMutation({
    mutationFn: () =>
      api.updateRecruiterQuotas(detail.recruiter_id, {
        daily_dm_quota: Number(daily),
        monthly_launch_quota: monthly ? Number(monthly) : undefined,
        checkin_hour: Number(hour),
        checkin_timezone: tz,
      }),
    onSuccess: () => onSaved(),
  });

  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      <Field label="Daily DM quota" value={daily} onChange={setDaily} />
      <Field label="Monthly launch quota" value={monthly} onChange={setMonthly} />
      <Field label="Check-in hour (0-23)" value={hour} onChange={setHour} />
      <Field label="Timezone" value={tz} onChange={setTz} />
      <div className="col-span-2">
        <Button
          size="sm" variant="outline"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (s: string) => void }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
